import { REVIEW_TEST_IDS } from '@/components/admin/sessions/contracts';
import { reviewTrialState, reviewTrialWhy } from '@/lib/review-trial';
import { NOYAU_TRIALS_KEY, NOYAU_TRIAL_KEY, fetchNoyauTrial } from '@/lib/session-api';
import { costLabel } from '@/lib/session-labels';
import { NOYAU_TRIAL_REFUSAL_SENTENCE, noyauTrialRefusalOf } from '@/lib/session-noyau';
import { startReviewTrial } from '@/lib/session-review-api';
import type { NoyauTrial } from '@matter_extractor/shared';
import { Button, LoadingRing } from '@pms-digital/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * « Tester l'analyste sur cette relecture » (relecture.md §11, D16): the one
 * shortcut the review keeps to the blank analyst.
 *
 * It starts a trial on the source « Relecture » - the extracts of the review at
 * the moment of the press, on the targets of its grid - and shows only the state
 * of the launch: « L'analyste lit cette relecture… » while it runs, then « Essai
 * rendu · 0,06 € · voir l'essai dans le parcours ». The six notes are never drawn
 * here (D4): the trial is kept with the session, source included, and read there.
 *
 * ## Why the page reads the trial again
 *
 * The same reason as the parcours: the analyst may think for minutes and the
 * proxy cuts a request at sixty seconds, so the start answers at once with the
 * trial PENDING, seeded under its key, and the page reads it every 1,5 s until
 * it settles. A read that FAILS stops the reading, and frees the button.
 *
 * It is closed - really `disabled`, with its sentence beside it - while nothing is
 * ranged, and in the trash.
 */

const TRIAL_POLL_MS = 1500;

export interface ReviewTrialProps {
  readonly sessionId: string;
  readonly extractCount: number;
  /** The session is in the trash: the review is read-only and so is the shortcut. */
  readonly trashed: boolean;
}

export function ReviewTrial({ sessionId, extractCount, trashed }: ReviewTrialProps) {
  const client = useQueryClient();
  const [trialId, setTrialId] = useState<string | null>(null);

  const start = useMutation({
    mutationFn: () => startReviewTrial(sessionId),
    onSuccess: (trial) => {
      // Seeded, so the page says « lit cette relecture » at once: the reads that
      // follow only move it forward.
      client.setQueryData(NOYAU_TRIAL_KEY(sessionId, trial.id), trial);
      setTrialId(trial.id);
      void client.invalidateQueries({ queryKey: NOYAU_TRIALS_KEY(sessionId) });
    },
  });

  const key = NOYAU_TRIAL_KEY(sessionId, trialId ?? '');
  const shown = useQuery({
    queryKey: key,
    queryFn: async () => {
      const before = client.getQueryData<NoyauTrial>(key);
      const trial = await fetchNoyauTrial(sessionId, trialId ?? '');
      if (before?.determination.state === 'pending' && trial.determination.state !== 'pending') {
        void client.invalidateQueries({ queryKey: NOYAU_TRIALS_KEY(sessionId) });
      }
      return trial;
    },
    enabled: trialId !== null,
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    refetchInterval: (query) =>
      query.state.status !== 'error' && query.state.data?.determination.state === 'pending'
        ? TRIAL_POLL_MS
        : false,
  });

  const state = reviewTrialState({
    starting: start.isPending,
    trial: start.isError ? undefined : shown.data,
    readFailed: shown.isError,
  });
  const refusal = noyauTrialRefusalOf(start.error);
  const why = reviewTrialWhy({ extractCount, trashed });

  const press = (
    <Button
      type="button"
      variant="accent"
      size="sm"
      data-testid={REVIEW_TEST_IDS.trial}
      disabled={why !== null || state === 'running'}
      compact={true}
      onClick={() => start.mutate()}
    >
      {state === 'rendered' || state === 'failed'
        ? 'Relancer l’analyste sur cette relecture'
        : 'Tester l’analyste sur cette relecture'}
    </Button>
  );

  return (
    <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
      <span
        data-testid={REVIEW_TEST_IDS.trialState}
        data-state={state}
        className="inline-flex flex-wrap items-center gap-x-2 text-caption text-gray-700"
      >
        {state === 'running' ? (
          <>
            <span className="inline-flex text-bleu-nuit">
              <LoadingRing size="sm" />
            </span>
            L’analyste lit cette relecture…
          </>
        ) : state === 'rendered' ? (
          <>
            Essai rendu
            {shown.data === undefined ? null : ` · ${costLabel(shown.data.determination.costEur)}`}
            {' · '}
            <TrialLink sessionId={sessionId} label="voir l’essai dans le parcours" />
          </>
        ) : state === 'failed' ? (
          <>
            L’essai n’a pas abouti ·{' '}
            <TrialLink sessionId={sessionId} label="voir dans le parcours" />
          </>
        ) : null}
      </span>
      {state === 'running' ? null : press}
      {refusal === null ? null : (
        <p
          role="alert"
          data-testid={REVIEW_TEST_IDS.trialRefusal}
          data-code={refusal}
          className="basis-full text-right text-caption text-gray-700"
        >
          {NOYAU_TRIAL_REFUSAL_SENTENCE[refusal]}
        </p>
      )}
      {refusal === null && start.isError ? (
        <p role="alert" className="basis-full text-right text-caption text-rouge">
          L’analyste n’a pas pu être lancé. Réessayez.
        </p>
      ) : null}
      {state === 'idle' && why !== null ? (
        <p
          data-testid={REVIEW_TEST_IDS.trialWhy}
          className="basis-full text-right text-gray-600 text-caption"
        >
          {why}
        </p>
      ) : null}
    </div>
  );
}

function TrialLink({ sessionId, label }: { readonly sessionId: string; readonly label: string }) {
  return (
    <Link
      to="/admin/sessions/$sessionId"
      params={{ sessionId }}
      data-testid={REVIEW_TEST_IDS.trialLink}
      className="font-semibold text-bleu-nuit underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
    >
      {label}
    </Link>
  );
}
